import React from 'react';
import { FormProvider } from 'react-hook-form';
import { Pressable, StatusBar } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { ProfileEditForm } from '@/features/guardian-profile/components';
import { useGuardianProfileEdit } from '@/features/guardian-profile/hooks';
import { createStyles, useGuardianStackNavigation, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';

export function GuardianProfileEditScreen() {
  const { scale } = useScaling();
  const insets = useSafeAreaInsets();
  const navigation = useGuardianStackNavigation();
  const styles = useStyles();
  const { form, submit, handlePickAvatar } = useGuardianProfileEdit({
    onSuccess: navigation.goBack,
  });
  const canSave = form.formState.isDirty;

  return (
    <LinearGradient
      colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
      locations={[0.08, 0.47, 0.91]}
      useAngle
      angle={210.65}
      style={styles.root}>
      <StatusBar barStyle="dark-content" backgroundColor="#EEF3FF" />
      <FormProvider {...form}>
        <Stack flex={1}>
          <Stack
            direction="row"
            align="center"
            justify="space-between"
            style={[styles.header, { paddingTop: insets.top + scale(4) }]}>
            <Pressable onPress={navigation.goBack} style={styles.headerButton}>
              <SvgIcon
                name="IcChevronRight"
                size={scale(24)}
                color={guardianColors['10']}
                style={styles.backIcon}
              />
            </Pressable>
            <Text variant="Font Guardian App/Body/Body 16 Bold">
              {STRINGS.guardianProfile.settings.account}
            </Text>
            <Pressable disabled={!canSave} onPress={submit} style={styles.headerSave}>
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                style={canSave ? styles.saveEnabled : styles.saveDisabled}>
                {STRINGS.guardianProfile.edit.save}
              </Text>
            </Pressable>
          </Stack>
          <KeyboardAwareScrollView
            style={styles.root}
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            bottomOffset={scale(12)}
            extraKeyboardSpace={scale(12)}>
            <ProfileEditForm
              onPickAvatar={handlePickAvatar}
              onDeleteAccount={() => navigation.navigate('GuardianDeleteAccount')}
            />
          </KeyboardAwareScrollView>
        </Stack>
      </FormProvider>
    </LinearGradient>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: { flex: 1 },
  header: { minHeight: scale(48), paddingHorizontal: scale(20), paddingBottom: scale(4) },
  headerButton: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.3)',
  },
  backIcon: { transform: [{ rotate: '180deg' }] },
  headerSave: { width: scale(40), alignItems: 'flex-end' },
  saveEnabled: { color: guardianColors['Main/Main'] },
  saveDisabled: { color: guardianColors['4'] },
  content: { paddingHorizontal: scale(16), paddingTop: scale(4), paddingBottom: scale(50) },
}));
